import React, { useState, useEffect, useMemo } from 'react';
import { apiClient } from '../../utils/apiClient';
import NotionStatsModal, { calcDifficultyRatio } from './NotionStatsModal';

const BASE = process.env.REACT_APP_API_BASE_URL;

function successColor(rate) {
    if (rate === null) return 'text-gray-400';
    if (rate < 40) return 'text-red-500 dark:text-red-400';
    if (rate < 70) return 'text-orange-500 dark:text-orange-400';
    return 'text-emerald-500 dark:text-emerald-400';
}

function successBarColor(rate) {
    if (rate === null) return 'bg-gray-300 dark:bg-gray-600';
    if (rate < 40) return 'bg-red-500';
    if (rate < 70) return 'bg-orange-400';
    return 'bg-emerald-500';
}

function calcAvgSuccess(items) {
    const with_ = items.filter(i => i.total_attempts > 0);
    return with_.length
        ? Math.round(with_.reduce((s, i) => s + i.success_rate, 0) / with_.length)
        : null;
}

function SummaryCard({ label, value, color = 'fuchsia' }) {
    const styles = {
        fuchsia: 'from-fuchsia-500/10 to-fuchsia-500/5 border-fuchsia-200 dark:border-fuchsia-500/20 text-fuchsia-600 dark:text-fuchsia-400',
        red:     'from-red-500/10 to-red-500/5 border-red-200 dark:border-red-500/20 text-red-600 dark:text-red-400',
        emerald: 'from-emerald-500/10 to-emerald-500/5 border-emerald-200 dark:border-emerald-500/20 text-emerald-600 dark:text-emerald-400',
        orange:  'from-orange-500/10 to-orange-500/5 border-orange-200 dark:border-orange-500/20 text-orange-600 dark:text-orange-400',
    };
    const textClass = styles[color].split(' ').find(c => c.startsWith('text-'));
    return (
        <div className={`bg-gradient-to-br ${styles[color]} border rounded-2xl px-5 py-4`}>
            <p className="text-xs text-gray-500 dark:text-gray-400 font-medium mb-1">{label}</p>
            <p className={`text-2xl font-bold ${textClass}`}>{value}</p>
        </div>
    );
}

// ── Notion card ───────────────────────────────────────────────────────────────
function NotionCard({ notion, onClick }) {
    const quizAvg     = calcAvgSuccess(notion.quizzes);
    const fcAvg       = calcAvgSuccess(notion.flashcards);
    const diff        = calcDifficultyRatio([...notion.quizzes, ...notion.flashcards]);
    const reportCount = notion.reports.length;
    const totalAttempts =
        notion.quizzes.reduce((s, q) => s + q.total_attempts, 0) +
        notion.flashcards.reduce((s, f) => s + f.total_attempts, 0);

    return (
        <div onClick={onClick}
            className="bg-white dark:bg-gray-900 rounded-2xl border border-gray-200 dark:border-white/10 p-5 cursor-pointer hover:border-fuchsia-300 dark:hover:border-fuchsia-500/30 hover:shadow-sm transition-all group">
            <div className="flex items-start justify-between gap-2 mb-4">
                <h3 className="font-semibold text-gray-900 dark:text-white text-sm leading-snug flex-1">{notion.name}</h3>
                <div className="flex items-center gap-1.5 shrink-0">
                    {reportCount > 0 && (
                        <span className="inline-flex items-center justify-center w-5 h-5 rounded-full bg-red-500 text-white text-[10px] font-bold">{reportCount}</span>
                    )}
                    <svg className="w-4 h-4 text-gray-300 group-hover:text-fuchsia-400 transition-colors" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                    </svg>
                </div>
            </div>

            <div className="grid grid-cols-2 gap-4">
                <div>
                    <p className="text-[10px] font-semibold text-gray-400 uppercase tracking-wider mb-1">Quiz</p>
                    <p className="text-2xl font-bold text-gray-800 dark:text-gray-200 leading-none mb-2">{notion.quizzes.length}</p>
                    {quizAvg !== null ? (
                        <div className="space-y-0.5">
                            <div className="flex items-center justify-between text-[10px]">
                                <span className="text-gray-400">Réussite</span>
                                <span className={`font-bold ${successColor(quizAvg)}`}>{quizAvg}%</span>
                            </div>
                            <div className="bg-gray-100 dark:bg-white/5 rounded-full h-1 overflow-hidden">
                                <div className={`${successBarColor(quizAvg)} h-1 rounded-full`} style={{ width: `${quizAvg}%` }} />
                            </div>
                        </div>
                    ) : <p className="text-[10px] text-gray-300 dark:text-gray-600">Pas de données</p>}
                </div>
                <div>
                    <p className="text-[10px] font-semibold text-gray-400 uppercase tracking-wider mb-1">Flashcards</p>
                    <p className="text-2xl font-bold text-gray-800 dark:text-gray-200 leading-none mb-2">{notion.flashcards.length}</p>
                    {fcAvg !== null ? (
                        <div className="space-y-0.5">
                            <div className="flex items-center justify-between text-[10px]">
                                <span className="text-gray-400">Réussite</span>
                                <span className={`font-bold ${successColor(fcAvg)}`}>{fcAvg}%</span>
                            </div>
                            <div className="bg-gray-100 dark:bg-white/5 rounded-full h-1 overflow-hidden">
                                <div className={`${successBarColor(fcAvg)} h-1 rounded-full`} style={{ width: `${fcAvg}%` }} />
                            </div>
                        </div>
                    ) : <p className="text-[10px] text-gray-300 dark:text-gray-600">Pas de données</p>}
                </div>
            </div>

            {/* Mini difficulty bar */}
            {diff && (
                <div className="mt-3 pt-3 border-t border-gray-100 dark:border-white/5 space-y-1">
                    <div className="flex h-1.5 rounded-full overflow-hidden gap-px">
                        <div className="bg-red-400"     style={{ width: `${Math.round(diff.hard   / diff.total * 100)}%` }} />
                        <div className="bg-orange-400"  style={{ width: `${Math.round(diff.medium / diff.total * 100)}%` }} />
                        <div className="bg-emerald-400" style={{ width: `${Math.round(diff.easy   / diff.total * 100)}%` }} />
                    </div>
                    <p className="text-[10px] text-gray-400">{totalAttempts.toLocaleString()} tentatives</p>
                </div>
            )}
        </div>
    );
}

// ── AdminStats (main) ─────────────────────────────────────────────────────────
export default function AdminStats() {
    const [quizData, setQuizData]         = useState(null);
    const [fcData, setFcData]             = useState(null);
    const [reportsData, setReportsData]   = useState(null);
    const [loading, setLoading]           = useState(true);
    const [error, setError]               = useState(null);
    const [selectedNotionId, setSelectedNotionId] = useState(null);
    const [search, setSearch]             = useState('');

    useEffect(() => {
        const loadAll = async () => {
            try {
                const [qRes, fRes, rRes] = await Promise.all([
                    apiClient(`${BASE}/admin_stats.php?action=quiz`),
                    apiClient(`${BASE}/admin_stats.php?action=flashcard`),
                    apiClient(`${BASE}/admin_stats.php?action=reports`),
                ]);
                const [q, f, r] = await Promise.all([qRes.json(), fRes.json(), rRes.json()]);
                if (q.success) setQuizData(q.data);
                if (f.success) setFcData(f.data);
                if (r.success) setReportsData(r.data);
                if (!q.success || !f.success || !r.success) setError('Erreur lors du chargement.');
            } catch {
                setError('Erreur de connexion.');
            }
            setLoading(false);
        };
        loadAll();
    }, []);

    const notions = useMemo(() => {
        const map = new Map();
        (quizData || []).forEach(q => {
            const id = q.notion_id;
            if (!map.has(id)) map.set(id, { id, name: q.notion_name || 'Sans notion', quizzes: [], flashcards: [], reports: [] });
            map.get(id).quizzes.push(q);
        });
        (fcData || []).forEach(f => {
            const id = f.notion_id;
            if (!map.has(id)) map.set(id, { id, name: f.notion_name || 'Sans notion', quizzes: [], flashcards: [], reports: [] });
            map.get(id).flashcards.push(f);
        });
        (reportsData || []).forEach(r => {
            const id = parseInt(r.notion_id);
            if (map.has(id)) map.get(id).reports.push(r);
        });
        return [...map.values()].sort((a, b) => a.name.localeCompare(b.name, 'fr'));
    }, [quizData, fcData, reportsData]);

    const selectedNotion = useMemo(
        () => notions.find(n => n.id === selectedNotionId) || null,
        [notions, selectedNotionId]
    );

    const totalReports  = (reportsData || []).length;
    const totalQuizzes  = (quizData || []).length;
    const totalFc       = (fcData || []).length;
    const totalAttempts = [
        ...(quizData || []).map(q => q.total_attempts),
        ...(fcData || []).map(f => f.total_attempts),
    ].reduce((s, n) => s + n, 0);

    const filteredNotions = notions.filter(n =>
        !search || n.name.toLowerCase().includes(search.toLowerCase())
    );

    if (loading) {
        return (
            <div className="flex items-center justify-center h-64 gap-3 text-gray-400">
                <div className="w-6 h-6 border-2 border-fuchsia-500 border-t-transparent rounded-full animate-spin" />
                <span className="text-sm">Chargement…</span>
            </div>
        );
    }

    if (error) {
        return (
            <div className="p-6 md:p-8">
                <div className="p-4 rounded-xl bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-500/30 text-red-600 dark:text-red-400 text-sm">{error}</div>
            </div>
        );
    }

    return (
        <div className="p-6 md:p-8 max-w-6xl mx-auto space-y-6 animate-fade-in">
            <div className="flex items-start justify-between gap-4 flex-wrap">
                <div>
                    <h1 className="text-2xl font-bold text-gray-900 dark:text-white">Statistiques</h1>
                    <p className="text-sm text-gray-500 mt-1">Performances par notion — cliquez sur une carte pour le détail.</p>
                </div>
                {totalReports > 0 && (
                    <div className="flex items-center gap-2 px-4 py-2 rounded-xl bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-500/20">
                        <span className="inline-flex items-center justify-center w-5 h-5 rounded-full bg-red-500 text-white text-[10px] font-bold">{totalReports}</span>
                        <span className="text-sm text-red-700 dark:text-red-400 font-medium">signalement{totalReports !== 1 ? 's' : ''} en attente</span>
                    </div>
                )}
            </div>

            <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                <SummaryCard label="Notions" value={notions.length} color="fuchsia" />
                <SummaryCard label="Questions quiz" value={totalQuizzes} color="fuchsia" />
                <SummaryCard label="Flashcards" value={totalFc} color="fuchsia" />
                <SummaryCard label="Tentatives totales" value={totalAttempts.toLocaleString()} color="fuchsia" />
            </div>

            <div className="flex items-center gap-3">
                <input
                    value={search} onChange={e => setSearch(e.target.value)}
                    placeholder="Filtrer par notion…"
                    className="flex-1 max-w-sm px-3 py-2 rounded-xl border border-gray-200 dark:border-white/10 bg-white dark:bg-gray-800 text-sm text-gray-800 dark:text-gray-200 focus:outline-none focus:ring-2 focus:ring-fuchsia-400"
                />
                <span className="text-xs text-gray-400">{filteredNotions.length} notion{filteredNotions.length !== 1 ? 's' : ''}</span>
            </div>

            {filteredNotions.length === 0 ? (
                <p className="text-center py-16 text-gray-400">Aucune notion trouvée.</p>
            ) : (
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
                    {filteredNotions.map(notion => (
                        <NotionCard
                            key={notion.id}
                            notion={notion}
                            onClick={() => setSelectedNotionId(notion.id)}
                        />
                    ))}
                </div>
            )}

            {selectedNotion && (
                <NotionStatsModal
                    notionId={selectedNotion.id}
                    notionName={selectedNotion.name}
                    onClose={() => setSelectedNotionId(null)}
                />
            )}
        </div>
    );
}
